/**
 * Bulgarian Belot scoring and match end (rules §7, §8).
 *
 * Each side's count for the hand is its card points, ten for the last trick,
 * the declarations it won, twenty for each Belot, and ninety for a valat (all
 * eight tricks). No Trumps doubles the cards and the last trick.
 *
 *  - The contract side has more: both sides keep their own count.
 *  - It has less ("inside"): the other side takes both counts.
 *  - Equal ("hanging"): the other side keeps its own; the contract side's
 *    count waits for whoever wins the next hand.
 *  - Contra or recontra: whichever side has more takes both counts, doubled or
 *    quadrupled; a tie leaves the lot hanging.
 *
 * Counts become game points by dividing by ten, with the rounding each
 * contract's total calls for (§7.3), so a hand is always worth 16 in a suit,
 * 26 in All Trumps and 26 in No Trumps before declarations.
 */
import { SUIT_LABEL, rankOf, suitOf } from '../../core/cards.js';
import { BELOT_BELOT_POINTS, BELOT_LAST_TRICK, BELOT_TRICKS_PER_HAND, BELOT_VALAT_BONUS, } from './config.js';
import { belotCardsDealt, resolveBelotDeclarations } from './declarations.js';
import { belotPoints } from './rules.js';
const KIND_NAME = {
    tierce: 'Tierce', quarte: 'Quarte', quint: 'Quint', carre: 'Carré',
};
const CARRE_OF = { 11: 'Jacks', 9: 'Nines', 14: 'Aces', 10: 'Tens', 13: 'Kings', 12: 'Queens' };
export function belotDeclarationText(d) {
    if (d.kind === 'carre')
        return `Carré of ${CARRE_OF[rankOf(d.cards[0]) ?? 0] ?? ''}`;
    const suit = suitOf(d.cards[0]);
    return `${KIND_NAME[d.kind]} ${suit ? SUIT_LABEL[suit] : ''}`;
}
/**
 * Rules §7.3: a count to game points. `more` says whether this side has the
 * larger count of the two, which only matters on the digit the two sides'
 * remainders have to share:
 *
 *  - suit contracts (total ends in 2): the larger rounds up from 7, the
 *    smaller from 6 — 86:76 is 8:8;
 *  - All Trumps (total ends in 8): up from 5, and a 4 rounds up only for the
 *    smaller side — 134:124 is 13:13;
 *  - No Trumps (already doubled, total ends in 0): halves up.
 */
export function toGamePoints(count, mode, more) {
    const tens = Math.floor(count / 10);
    const r = count % 10;
    if (mode === 'NT')
        return tens + (r >= 5 ? 1 : 0);
    if (mode === 'AT')
        return tens + (r >= 5 || (r === 4 && !more) ? 1 : 0);
    return tens + (r >= (more ? 7 : 6) ? 1 : 0);
}
export function belotTally(hand, table, config) {
    const mode = hand.contract.mode;
    const cardPoints = table.teams.map(() => 0);
    const tricksByTeam = table.teams.map(() => 0);
    for (const t of hand.tricks) {
        if (t.winner === null)
            continue;
        const team = table.teamOf[t.winner];
        tricksByTeam[team] += 1;
        for (const p of t.plays)
            cardPoints[team] += belotPoints(p.card, mode);
    }
    const last = hand.tricks[BELOT_TRICKS_PER_HAND - 1]?.winner ?? null;
    const valat = tricksByTeam.findIndex((n) => n === BELOT_TRICKS_PER_HAND);
    const belotsByTeam = table.teams.map(() => 0);
    for (const b of hand.extra.belots)
        belotsByTeam[table.teamOf[b.seat]] += BELOT_BELOT_POINTS;
    return {
        cardPoints,
        lastTrickTeam: last === null ? null : table.teamOf[last],
        valatTeam: valat === -1 ? null : valat,
        decl: config.declarations
            ? resolveBelotDeclarations(belotCardsDealt(hand.tricks, hand.secret.holdings), mode, table)
            : { all: [], pointsByTeam: table.teams.map(() => 0) },
        belotsByTeam,
    };
}
export function belotScoreHand(hand, table, config, matchExtra) {
    const tricksBySeat = Array.from({ length: table.seatCount }, () => 0);
    for (const t of hand.tricks)
        if (t.winner !== null)
            tricksBySeat[t.winner] += 1;
    const reserve = matchExtra?.hanging ?? 0;
    const deltas = table.teams.map(() => 0);
    const contract = hand.contract;
    if (!contract) {
        return { deltas, tricksBySeat, contractMade: null, lines: [], matchExtra: { hanging: reserve, lastHand: null } };
    }
    const mode = contract.mode;
    const tally = belotTally(hand, table, config);
    const T = table.teamOf[contract.taker];
    const D = table.teams.find((t) => t.id !== T).id;
    const doubling = mode === 'NT' ? 2 : 1;
    // Each side's count, and the words for it.
    const parts = (t) => {
        const text = [];
        let count = 0;
        const cards = tally.cardPoints[t] * doubling;
        if (cards > 0) {
            count += cards;
            text.push(`cards ${cards}`);
        }
        if (tally.lastTrickTeam === t) {
            count += BELOT_LAST_TRICK * doubling;
            text.push(`last trick ${BELOT_LAST_TRICK * doubling}`);
        }
        for (const d of tally.decl.all) {
            if (d.counts && table.teamOf[d.seat] === t) {
                count += d.points;
                text.push(`${belotDeclarationText(d).toLowerCase()} ${d.points}`);
            }
        }
        if (tally.belotsByTeam[t] > 0) {
            const n = tally.belotsByTeam[t] / BELOT_BELOT_POINTS;
            count += tally.belotsByTeam[t];
            text.push(n > 1 ? `${n} belots ${tally.belotsByTeam[t]}` : `belot ${BELOT_BELOT_POINTS}`);
        }
        if (tally.valatTeam === t) {
            count += BELOT_VALAT_BONUS;
            text.push(`valat ${BELOT_VALAT_BONUS}`);
        }
        return { count, text };
    };
    const pt = parts(T);
    const pd = parts(D);
    const sentence = (p) => (p.text.length > 0 ? `${p.count}: ${p.text.join(', ')}` : '0');
    const lines = [];
    const score = table.teams.map(() => 0);
    let made;
    let hangs = 0;
    const mult = contract.mult;
    if (mult === 1) {
        if (pt.count > pd.count) {
            made = true;
            score[T] = toGamePoints(pt.count, mode, true);
            score[D] = toGamePoints(pd.count, mode, false);
            lines.push({ team: T, text: sentence(pt), points: score[T] });
            lines.push({ team: D, text: sentence(pd), points: score[D] });
        }
        else if (pt.count < pd.count) {
            made = false;
            const all = pt.count + pd.count;
            score[D] = toGamePoints(all, mode, true);
            lines.push({ team: T, text: `Inside with ${sentence(pt)}`, points: 0 });
            lines.push({ team: D, text: `Takes all ${all}`, points: score[D] });
        }
        else {
            made = null;
            score[D] = toGamePoints(pd.count, mode, true);
            hangs = toGamePoints(pt.count, mode, false);
            lines.push({ team: D, text: sentence(pd), points: score[D] });
            lines.push({ team: T, text: `Hanging: ${hangs} wait for the next hand`, points: 0 });
        }
    }
    else {
        const all = (pt.count + pd.count) * mult;
        const word = mult === 4 ? 'Recontra ×4' : 'Contra ×2';
        if (pt.count === pd.count) {
            made = null;
            hangs = toGamePoints(all, mode, true);
            lines.push({ team: T, text: `${word}, tied: ${hangs} wait for the next hand`, points: 0 });
        }
        else {
            made = pt.count > pd.count;
            const W = made ? T : D;
            const L = made ? D : T;
            score[W] = toGamePoints(all, mode, true);
            lines.push({ team: L, text: sentence(made ? pd : pt), points: 0 });
            lines.push({ team: W, text: `${word}: takes all ${all}`, points: score[W] });
        }
    }
    // Points left hanging go to whoever wins this hand; another tie adds to them.
    let carry = reserve + hangs;
    if (reserve > 0 && made !== null) {
        const winner = made ? T : D;
        score[winner] += reserve;
        lines.push({ team: winner, text: 'Hanging points from before', points: reserve });
        carry = hangs;
    }
    for (const t of [T, D])
        deltas[t] = score[t];
    return {
        deltas,
        tricksBySeat,
        contractMade: made,
        lines,
        matchExtra: { hanging: carry, lastHand: { valat: tally.valatTeam !== null } },
    };
}
/**
 * First to the target; if both cross on the same hand the higher total wins,
 * and a tie plays on. A match is never won on a valat: one more hand.
 */
export function belotMatchEnd(state, config) {
    const scores = state.scores;
    const over = state.table.teams.filter((t) => (scores[t.id] ?? 0) >= config.target);
    if (over.length === 0)
        return { finished: false };
    const best = over.reduce((a, b) => ((scores[b.id] ?? 0) > (scores[a.id] ?? 0) ? b : a));
    if (state.table.teams.some((t) => t.id !== best.id && scores[t.id] === scores[best.id]))
        return { finished: false };
    if (state.extra?.lastHand?.valat)
        return { finished: false };
    return { finished: true, winningTeam: best.id, reason: `Reached ${scores[best.id]}` };
}
//# sourceMappingURL=scoring.js.map